import { useUpdateScreenChatContext } from '$tablet-app/src/app/chat/hooks';
import { ScreenBackButton } from '$tablet-app/src/app/components/ScreenBackButton';
import { ScreenHeader } from '$tablet-app/src/app/components/ScreenHeader';
import { ScreenTitle } from '$tablet-app/src/app/components/ScreenTitle';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { RootStackScreenProps } from '$tablet-app/src/app/navigation/RootStackNavigator';
import { palette } from '$tablet-app/src/app/theme';
import { showErrorToast } from '$tablet-app/src/app/toasts';
import { spacing } from '@akin-home/theme';
import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import styled from 'styled-components/native';
import { getRobotDetails } from '../../info/api';
import { RobotDevice } from '../../types';
import { decodeBleServiceData, getRobotTypeFromId } from '../../utils';
import { isRobotAccessible } from '../functions';
import { BT_GATT_UUIDS } from '../types';
import { useBleScanner } from '../useBleScanner';
import { BluetoothScanButton } from './BluetoothScanButton';
import { ConnectToRobotComponent } from './ConnectToRobotComponent';
import { NearbyRobotDisplay } from './NearbyRobotDisplay';
import { RobotDirectConnectInput } from './RobotDirectConnectInput';

export const BluetoothRobotConnectScreen = (
  props: RootStackScreenProps<'RobotConnect'>
) => {
  const {
    navigation: { goBack },
  } = props;

  useUpdateScreenChatContext();
  // useEffect(() => {
  //   sendBackgroundMessage(intent_labels.open_create_checklist_screen);
  // }, []);

  const {
    isScanning,
    devices,
    requestBluetoothPermission,
    scanDevices,
    stopScanDevices,
  } = useBleScanner();

  const onStartScanning = async () => {
    await requestBluetoothPermission();
    const result = await scanDevices(BT_GATT_UUIDS.SERVICE_AKIN_CONNECT);
    if (!result) {
      showErrorToast({
        text1: 'Scan Error',
        text2: `Please try again`,
      });
    }
  };

  const onStopScanning = () => {
    stopScanDevices();
  };

  const handleDirectConnect = async (ipAddress: string) => {
    console.log('Directly connecting to', ipAddress);

    const robotAvailable = await isRobotAccessible(ipAddress);

    if (!robotAvailable) {
      showErrorToast({
        text1: 'Robot not found',
        text2: 'No robot was found at this address. Please try again.',
      });
      setSelectedRobot(undefined);
    } else {
      let robotDetails;
      try {
        robotDetails = await getRobotDetails(ipAddress);
      } catch (err) {
        console.error('Error in getting robot details', err);
      }

      const robotDevice: RobotDevice = {
        id: robotDetails?.id ?? 'robot',
        name: robotDetails?.name ?? 'robot',
        type: getRobotTypeFromId(robotDetails?.type?.id) ?? 'indi_v0',
        websocketConnection: {
          connectionStatus: 'disconnected',
          ipAddress: ipAddress,
        },
      };

      setSelectedRobot(robotDevice);
    }
  };

  const nearbyRobots = useMemo<RobotDevice[]>(
    () => [
      ...devices.map((device) => {
        const deviceDetails = decodeBleServiceData(device.serviceData);

        return {
          id: deviceDetails?.robotId ?? device.id,
          name: device.localName ?? 'unnamed',
          type: deviceDetails?.type ?? 'aly_v1',
          bluetoothConnection: {
            bluetoothDevice: device,
          },
        } as RobotDevice;
      }),
    ],
    [devices]
  );

  const [selectedRobot, setSelectedRobot] = useState<RobotDevice>();

  const handleSelectRobot = useCallback(
    (robotDevice: RobotDevice | undefined): void => {
      setSelectedRobot(robotDevice);
    },
    []
  );

  return (
    <>
      <Screen>
        <Header>
          <ScreenBackButton onPress={goBack} />
          <ScreenTitle>Connect to Akin Robots</ScreenTitle>
        </Header>

        <View
          style={{
            flex: 1,
            width: '100%',
            flexDirection: 'row',
          }}
        >
          <View
            style={{
              flexDirection: 'column',
              margin: spacing[1],
              padding: spacing[1],
              flexGrow: 0,
              flexShrink: 0,
            }}
          >
            <View style={{ flex: 1 }}>
              <Text
                style={{
                  marginHorizontal: spacing[3],
                  fontSize: 20,
                  color: 'black',
                }}
              >
                <Text style={{ fontWeight: 'bold' }}>{'Nearby Robots: '}</Text>
                <Text>{`${nearbyRobots.length} Found`}</Text>
              </Text>

              <View
                style={{
                  flex: 1,
                  padding: spacing[2],
                  flexDirection: 'column',
                }}
              >
                <NearbyRobotDisplay
                  robotDevices={nearbyRobots}
                  onSelectRobot={handleSelectRobot}
                />

                <BluetoothScanButton
                  isScanning={isScanning}
                  onStartScanning={onStartScanning}
                  onStopScanning={onStopScanning}
                />
              </View>
            </View>

            <View style={styles.horizontalSpacerBar} />

            <View>
              <Text
                style={{
                  marginHorizontal: spacing[3],
                  marginTop: spacing[2],
                  fontSize: 20,
                  color: 'black',
                }}
              >
                <Text>➥ or Connect Directly:</Text>
              </Text>
              <RobotDirectConnectInput onSubmit={handleDirectConnect} />
            </View>
          </View>

          <View style={styles.verticalSpacerBar} />

          <View
            style={{
              flexDirection: 'column',
              margin: spacing[3],
              padding: spacing[1],
              backgroundColor: palette.Background_purple,
              borderRadius: 10,
              flex: 1,
            }}
          >
            <ConnectToRobotComponent robotDevice={selectedRobot} />
          </View>
        </View>
      </Screen>
      <DeveloperScreenInfo />
    </>
  );
};

const Screen = styled.View`
  flex: 1;
`;

const Header = styled(ScreenHeader)`
  justify-content: flex-start;
  gap: 20px;
`;

const styles = StyleSheet.create({
  horizontalSpacerBar: {
    backgroundColor: 'black',
    height: 2,
    marginHorizontal: spacing[2],
  },
  verticalSpacerBar: {
    backgroundColor: 'black',
    width: 2,
    marginVertical: spacing[2],
  },
});
